import { DownOutlineIcon, VOutlineIcon } from '@carboncopyco/icons';
import { RuleApplyOnType } from '@carboncopyco/types';
import {
  ColorHex,
  Dropdown,
  DropdownItem,
  TextBase,
  TextCaption,
  TooltipBubble,
} from '@carboncopyco/ui';
import { useBoolean, useClickAway } from 'ahooks';
import { useRef } from 'react';
import { InputField } from '../InputField';

export interface ApplyOnDropdownProps {
  applyOn: RuleApplyOnType;
  setApplyOn: (value: RuleApplyOnType) => void;
  currentPageUrl: string;
}

const applyOnTypeToLabel = {
  [RuleApplyOnType.allPages]: 'All pages',
  [RuleApplyOnType.currentPage]: 'This page only',
};

export const ApplyOnDropdown = ({
  applyOn,
  setApplyOn,
  currentPageUrl,
}: ApplyOnDropdownProps) => {
  const [isOpen, { set: setIsOpen, toggle: toggleOpen }] = useBoolean();
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  useClickAway(() => setIsOpen(false), dropdownContainerRef);

  return (
    <div
      className="flex flex-col gap-1"
      ref={dropdownContainerRef}
      data-edit-tool="true"
    >
      <InputField
        label="Apply To"
        name="applyOn"
      >
        <Dropdown
          align="end"
          interactive
          asChild
          container={dropdownContainerRef?.current}
          open={isOpen}
          onOpenChange={toggleOpen}
          width={272}
          triggerElement={
            <div
              className="flex border rounded-md border-ds-accent3-100 py-1 justify-between w-full p-2 border-ds-natural-150 items-center"
              data-ds-dropdown-id="applyOn"
            >
              <TextBase
                label={applyOnTypeToLabel[applyOn]}
                color="text-ds-natural-600"
              />
              <DownOutlineIcon color={ColorHex.primary300} />
            </div>
          }
        >
          {Object.entries(applyOnTypeToLabel).map(([applyAcrossKey, label]) => {
            return (
              <DropdownItem
                asChild
                key={applyAcrossKey}
                label={label}
                onClick={() => setApplyOn(applyAcrossKey as RuleApplyOnType)}
                iconRight={applyOn === applyAcrossKey ? <VOutlineIcon /> : null}
              />
            );
          })}
        </Dropdown>
      </InputField>
      {applyOn === RuleApplyOnType.currentPage && (
        <TooltipBubble
          title={currentPageUrl}
          placement="top"
          appendTo="parent"
          width={250}
          interactive
        >
          <div className="mt-4">
            <div className="flex flex-col gap-1">
              <TextCaption
                label="Page:"
                color="text-ds-natural-600"
              />
              <TextCaption
                label={currentPageUrl}
                ellipsis
              />
            </div>
          </div>
        </TooltipBubble>
      )}
    </div>
  );
};
